import React, { useState, useEffect, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate,
  useLocation,
  useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessGestionModule,
  canAccessTuWeb,
  BANDA_TU_WEB_TAB_PATH,
  } from '../utils/bandaGestionAccess';
import { hasBandaTour } from '../tours/tourRegistry';
import { useSocket } from '../contexts/SocketContext';
import { useMvpMode } from '../config/mvp.jsx';
import { trackLogout } from '../services/analytics';
import { mensajesAPI,
  notificacionesAPI } from '../services/api';
import Footer from './Footer';
import SupportWidget from './SupportWidget';
import {
  Box,
  Flex,
  HStack,
  VStack,
  Button,
  Text,
  Container,
  Icon,
  Image,
  Menu,
  Breadcrumb,
  Badge,
  Drawer,
  useDisclosure,
  IconButton,
  Stack,
  Spacer,
  Spinner,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaUser,
  FaCalendarAlt,
  FaSearch,
  FaEnvelope,
  FaSignOutAlt,
  FaChevronRight,
  FaHome,
  FaBell,
  FaBars,
  FaCalendarCheck,
  FaQuestionCircle,
  FaThList,
  FaMusic,
  FaFolderOpen,
  FaUsers,
  FaLayerGroup,
  FaAddressBook,
  FaDrum,
  FaChartLine,
  FaBullseye,
  FaFileAlt,
  FaKey,
  FaGlobe,
  FaPoll,
  FaCrown,
} from 'react-icons/fa';

const AppLayout = ({
  children,
  userType = 'banda',
  userName = 'Mi Banda',
  onNavigate = null,
  /** 'guestPublic': solo logo (→ landing) + pestaña Comunidad, sin menú de usuario */
  headerVariant = 'default',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { logout, profile, user, isAuthenticated, isTitularBanda, hasFeature } = useAuth();
  const isColaboradorBanda = user?.userType === 'BANDA_COLABORADOR';
  const { socket, isConnected } = useSocket();
  const { isMvpMode } = useMvpMode();
  const bg = useColorModeValue('white', 'gray.800');
  
  const isGuestPublic = headerVariant === 'guestPublic';
  
  // Función helper para manejar navegación (usa onNavigate si está disponible)
  const handleNavigation = (path) => {
    if (onNavigate) {
      onNavigate(path);
    } else {
      navigate(path);
    }
  };
  
  // Estado para mensajes no leídos
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Estado para notificaciones
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  
  // Obtener avatar del perfil
  const userAvatar = profile?.avatar || null;
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const { open, onOpen, onClose } = useDisclosure();
  const { open: isNotificationsOpen, onOpen: onNotificationsOpenBase, onClose: onNotificationsClose } = useDisclosure();
  const { open: isGestionOpen, onOpen: onGestionOpen, onClose: onGestionClose } = useDisclosure();

  // Cargar conteo de mensajes no leídos
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!isAuthenticated || user?.userType === 'BANDA_COLABORADOR') return;
      try {
        const response = await mensajesAPI.getConversaciones();
        const conversaciones = response.data || [];
        // Sumar mensajes no leídos de todas las conversaciones
        const totalUnread = conversaciones.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
        setUnreadMessages(totalUnread);
      } catch (error) {
        console.error('Error cargando mensajes no leídos:', error);
      }
    };

    loadUnreadCount();
    // Actualizar cada 30 segundos
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.userType]);

  // Cargar notificaciones
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated || user?.userType === 'BANDA_COLABORADOR') return;
    try {
      setIsLoadingNotifications(true);
      const [notifResponse, countResponse] = await Promise.all([
        notificacionesAPI.getAll({ limit: 20 }),
        notificacionesAPI.getUnreadCount()
      ]);
      setNotifications(notifResponse.data || []);
      setUnreadNotifications(countResponse.data?.count || 0);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [isAuthenticated, user?.userType]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Wrapper para abrir notificaciones y recargar
  const onNotificationsOpen = useCallback(() => {
    loadNotifications();
    onNotificationsOpenBase();
  }, [loadNotifications, onNotificationsOpenBase]);

  // Escuchar nuevas notificaciones por WebSocket
  useEffect(() => {
    if (!socket || !isConnected || user?.userType === 'BANDA_COLABORADOR') return;

    const handleNewNotification = (notification) => {
      // Añadir la nueva notificación al inicio
      setNotifications(prev => [notification, ...prev]);
      setUnreadNotifications(prev => prev + 1);

      // Mostrar toast
      toaster.create({
        title: notification.title || 'Nueva notificación',
        description: notification.message,
        type: 'info',
        duration: 5000,
        closable: true,
        position: 'top-right',
      });
    };

    socket.on('notification:new', handleNewNotification);

    return () => {
      socket.off('notification:new', handleNewNotification);
    };
  }, [socket, isConnected, user?.userType]);

  // Marcar todas las notificaciones como leídas
  const handleMarkAllAsRead = async () => {
    try {
      await notificacionesAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadNotifications(0);
    } catch (error) {
      console.error('Error marcando notificaciones como leídas:', error);
    }
  };

  // Eliminar notificación del drawer y del backend, luego navegar
  const handleNotificationClick = async (notification) => {
    // Eliminar la notificación del drawer inmediatamente (optimistic update)
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    if (!notification.isRead) {
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    }
    
    // Cerrar drawer y navegar primero para mejor UX
    if (notification.link) {
      onNotificationsClose();
      navigate(notification.link);
    }
    
    // Eliminar del backend en segundo plano
    try {
      await notificacionesAPI.delete(notification.id);
    } catch (error) {
      console.error('Error eliminando notificación:', error);
      // No recargar para no interrumpir la navegación
    }
  };

  // Obtener icono según el tipo de notificación
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'NEW_MESSAGE':
        return { icon: FaEnvelope, color: 'purple' };
      case 'EVENT_INVITE':
      case 'EVENT_REMINDER':
        return { icon: FaCalendarAlt, color: 'blue' };
      case 'NEW_REVIEW':
        return { icon: FaUser, color: 'green' };
      case 'VERIFICATION_STATUS':
        return { icon: FaUser, color: 'orange' };
      default:
        return { icon: FaBell, color: 'gray' };
    }
  };

  // Formatear tiempo relativo
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `Hace ${diffDays} día${diffDays > 1 ? 's' : ''}`;
    return date.toLocaleDateString();
  };

  // Prefijo de ruta según el tipo de usuario
  const routePrefix = userType === 'banda' ? '/banda' : '/cofradia';

  const navigationItems = [
    { label: 'Perfil', icon: FaUser, path: `${routePrefix}/perfil`, enabled: true },
    { label: 'Calendario', icon: FaCalendarAlt, path: `${routePrefix}/calendario`, enabled: userType === 'banda' && !isColaboradorBanda },
    { label: 'Eventos', icon: FaCalendarCheck, path: `${routePrefix}/eventos`, enabled: !isColaboradorBanda },
    { label: 'Comunidad', icon: FaSearch, path: `${routePrefix}/buscar`, enabled: !isColaboradorBanda },
    { label: 'Mensajes', icon: FaEnvelope, path: `${routePrefix}/mensajes`, badge: unreadMessages > 0 ? unreadMessages : null, enabled: !isColaboradorBanda },
  ];

  const gestionItems = [
    {
      label: 'Archivo Musical',
      icon: FaMusic,
      path: `${routePrefix}/repertorio`,
      desc: 'Partituras, obras y piezas del grupo',
      color: 'purple',
      permiso: 'repertorio',
      feature: 'archivoMusical',
    },
    {
      label: 'Mis músicos',
      icon: FaUsers,
      path: `${routePrefix}/musicos`,
      desc: 'Plantilla, instrumentos y asistencias',
      color: 'green',
      permiso: 'musicos',
      feature: 'musicos',
    },
    {
      label: 'Formaciones',
      icon: FaLayerGroup,
      path: `${routePrefix}/formaciones`,
      desc: 'Agrupaciones y configuraciones de sección',
      color: 'orange',
      permiso: 'formaciones',
      feature: 'formaciones',
    },
    {
      label: 'Actuaciones',
      icon: FaThList,
      path: `${routePrefix}/actuaciones`,
      desc: 'Gestiona conciertos, procesiones y eventos',
      color: 'blue',
      permiso: 'actuaciones',
      feature: 'actuaciones',
    },
    {
      label: 'Ensayos',
      icon: FaDrum,
      path: `${routePrefix}/ensayos`,
      desc: 'Programación y asistencia de ensayos',
      color: 'pink',
      permiso: 'ensayos',
      feature: 'ensayos',
    },
    {
      label: 'Documentos',
      icon: FaFileAlt,
      path: `${routePrefix}/documentos`,
      desc: 'Estatutos, actas, normativa y documentos con enlace seguro',
      color: 'purple',
      bandaOnly: true,
      permiso: 'documentos',
      feature: 'documentos',
    },
    {
      label: 'Contactos',
      icon: FaAddressBook,
      path: `${routePrefix}/contactos`,
      desc: 'Refuerzos, proveedores y entidades',
      color: 'teal',
      permiso: 'contactos',
      feature: 'contactos',
    },
    {
      label: 'Estadísticas',
      icon: FaChartLine,
      path: `${routePrefix}/metricas`,
      desc: 'Informes por periodo y tendencias del grupo',
      color: 'cyan',
      bandaOnly: true,
      permiso: 'estadisticas',
      feature: 'estadisticas',
    },
    {
      label: 'Objetivos',
      icon: FaBullseye,
      path: `${routePrefix}/objetivos`,
      desc: 'Compromiso por asistencia y metas por puntos',
      color: 'orange',
      bandaOnly: true,
      permiso: 'estadisticas',
      feature: 'objetivos',
    },
    {
      label: 'Encuestas',
      icon: FaPoll,
      path: `${routePrefix}/encuestas-consultas`,
      desc: 'Consultas, votaciones y encuestas a músicos',
      color: 'teal',
      permiso: 'encuestas',
      feature: 'encuestas',
    },
    {
      label: 'Permisos',
      icon: FaKey,
      path: `${routePrefix}/permisos`,
      desc: 'Usuarios delegados y acceso a Gestión / Tu web',
      color: 'purple',
      bandaOnly: true,
      titularOnly: true,
      feature: 'permisos',
    },
  ];

  const visibleGestionItems = gestionItems.filter((i) => {
    if (i.bandaOnly && userType !== 'banda') return false;
    if (i.titularOnly && !isTitularBanda) return false;
    if (i.permiso && !canAccessGestionModule(user, i.permiso)) return false;
    if (i.feature && !hasFeature(i.feature)) return false;
    return true;
  });
  const gestionPaths = visibleGestionItems.map((i) => i.path);
  const firstVisibleGestionPath = visibleGestionItems[0]?.path;
  const isGestionActive = gestionPaths.some(p => location.pathname.startsWith(p));
  const isTuWebTabActive =
    userType === 'banda' &&
    location.pathname === '/banda/perfil' &&
    searchParams.get('tab') === 'tuweb';
  const colabDefaultNavTarget =
    firstVisibleGestionPath ||
    (canAccessTuWeb(user) ? BANDA_TU_WEB_TAB_PATH : `${routePrefix}/perfil`);
  const showTuWebInColabHeader =
    userType === 'banda' && isColaboradorBanda && canAccessTuWeb(user);
  const showGestionInColabHeader =
    userType === 'banda' && isColaboradorBanda && Boolean(firstVisibleGestionPath);
  const colabEntityLabel =
    profile?.nombre || user?.nombre || userName || 'la entidad';

  const getCurrentPageTitle = () => {
    const path = location.pathname;

    if (isTuWebTabActive) return 'Tu web';
    if (path.includes('/perfil')) return userType === 'banda' ? 'Mi perfil de banda' : 'Mi perfil de cofradía';
    if (path.includes('/calendario')) return 'Calendario de disponibilidad';
    if (path.includes('/eventos')) return 'Mis Eventos';
    if (path.includes('/repertorio')) return 'Archivo Musical';
    if (path.includes('/documentos')) return 'Documentos';
    if (path.includes('/buscar')) return 'Comunidad';
    if (path.includes('/actuaciones')) return 'Actuaciones';
    if (path.includes('/mensajes')) return 'Mensajes';
    if (path.includes('/musicos')) return 'Mis músicos';
    if (path.includes('/formaciones')) return 'Formaciones';
    if (path.includes('/contactos')) return 'Contactos';
    if (path.includes('/ensayos')) return 'Ensayos';
    if (path.includes('/encuestas-consultas')) return 'Encuestas';
    if (path.includes('/metricas')) return 'Estadísticas';
    if (path.includes('/permisos')) return 'Permisos';
    
    return 'Dashboard';
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    const homePath = isColaboradorBanda
      ? colabDefaultNavTarget
      : `${routePrefix}/perfil`;
    const breadcrumbs = [{ label: 'Inicio', path: homePath }];

    if (path.includes('/calendario')) {
      breadcrumbs.push({ label: 'Calendario', path: `${routePrefix}/calendario` });
    } else if (path.includes('/eventos')) {
      breadcrumbs.push({ label: 'Eventos', path: `${routePrefix}/eventos` });
    } else if (path.includes('/buscar')) {
      breadcrumbs.push({ label: 'Comunidad', path: `${routePrefix}/buscar` });
    } else if (path.includes('/mensajes')) {
      breadcrumbs.push({ label: 'Mensajes', path: `${routePrefix}/mensajes` });
    } else if (path.includes('/repertorio')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Archivo Musical', path: `${routePrefix}/repertorio` });
    } else if (path.includes('/documentos')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Documentos', path: `${routePrefix}/documentos` });
    } else if (path.includes('/actuaciones')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Actuaciones', path: `${routePrefix}/actuaciones` });
    } else if (path.includes('/musicos')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Mis músicos', path: `${routePrefix}/musicos` });
    } else if (path.includes('/formaciones')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Formaciones', path: `${routePrefix}/formaciones` });
    } else if (path.includes('/contactos')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Contactos', path: `${routePrefix}/contactos` });
    } else if (path.includes('/ensayos')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Ensayos', path: `${routePrefix}/ensayos` });
    } else if (path.includes('/encuestas-consultas')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Encuestas', path: `${routePrefix}/encuestas-consultas` });
    } else if (path.includes('/metricas')) {
      breadcrumbs.push({ label: 'Gestión', path: null });
      breadcrumbs.push({ label: 'Estadísticas', path: `${routePrefix}/metricas` });
    } else if (path.includes('/perfil') && searchParams.get('tab') === 'tuweb') {
      breadcrumbs.push({ label: 'Tu web', path: BANDA_TU_WEB_TAB_PATH });
    }

    return breadcrumbs;
  };

  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
      {/* Header */}
      <Box
        bg={bg}
        borderBottom="1px"
        borderColor={borderColor}
        position="sticky"
        top={0}
        zIndex={1000}
        backdropFilter="blur(10px)"
      >
        <Container maxW="7xl">
          <Flex minH={16} py={2} alignItems="center" justifyContent="space-between" gap={3}>
            {/* Logo */}
            <HStack
              gap={3}
              alignItems="center"
              cursor="pointer"
              flexShrink={0}
              onClick={() => {
                if (isGuestPublic) navigate('/');
                else if (isColaboradorBanda && userType === 'banda') handleNavigation(colabDefaultNavTarget);
                else navigate(`${routePrefix}/perfil`);
              }}
            >
              <Image
                src="/logo y texto cadencya.png"
                alt="Cadencya"
                h={{ base: "45px", md: "60px" }}
                w="auto"
              />
              {isColaboradorBanda && userType === 'banda' && (
                <VStack align="start" gap={0} display={{ base: 'none', lg: 'flex' }} maxW="280px">
                  <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Modo colaborador
                  </Text>
                  <Text fontSize="sm" fontWeight="600" color="gray.700" lineClamp={1} title={colabEntityLabel}>
                    {colabEntityLabel}
                  </Text>
                </VStack>
              )}
            </HStack>

            {isGuestPublic ? (
              <HStack gap={2} flex={1} justify="flex-end">
                <Tooltip content="Ir a Comunidad" showArrow bg="gray.700" color="white" positioning={{
                  placement: "bottom"
                }}>
                  <Button
                    variant={location.pathname === '/comunidad' ? 'solid' : 'ghost'}
                    bg={location.pathname === '/comunidad' ? 'purple.600' : 'transparent'}
                    color={location.pathname === '/comunidad' ? 'white' : 'gray.700'}
                    size="md"
                    rounded="full"
                    onClick={() => navigate('/comunidad')}
                    fontWeight="600"
                    _hover={{
                      bg: location.pathname === '/comunidad' ? 'purple.700' : 'gray.100',
                      transform: 'scale(1.02)',
                    }}
                    transition="all 0.2s"><Icon asChild><FaSearch /></Icon>Comunidad
                                      </Button>
                </Tooltip>
              </HStack>
            ) : isColaboradorBanda && userType === 'banda' ? (
              <>
                <HStack
                  gap={1}
                  flex={1}
                  justify="center"
                  display={{ base: 'none', md: 'flex' }}
                >
                  {showGestionInColabHeader && (
                    <Tooltip
                      content={isMvpMode ? 'Próximamente disponible' : isGestionActive ? 'Sección activa' : 'Abrir Gestión'}
                      showArrow
                      bg="gray.700"
                      color="white"
                      positioning={{
                        placement: "bottom"
                      }}
                    >
                      <Box position="relative" display="inline-flex">
                        <Button
                          variant={isGestionActive && !isMvpMode ? 'solid' : 'ghost'}
                          bg={isGestionActive && !isMvpMode ? 'purple.600' : 'transparent'}
                          color={isMvpMode ? 'gray.400' : isGestionActive ? 'white' : 'gray.700'}
                          size="md"
                          rounded="full"
                          onClick={() => handleNavigation(firstVisibleGestionPath)}
                          cursor="pointer"
                          fontWeight="600"
                          opacity={isMvpMode ? 0.6 : 1}
                          _hover={{
                            bg: isMvpMode ? 'gray.100' : isGestionActive ? 'purple.600' : 'gray.100',
                            transform: 'scale(1.02)',
                          }}
                          _active={{ transform: 'scale(0.98)' }}
                          transition="all 0.2s"><Icon asChild><FaFolderOpen /></Icon>Gestión
                                                  </Button>
                        {isMvpMode && (
                          <Badge
                            position="absolute"
                            top="-6px"
                            right="-6px"
                            colorPalette="orange"
                            fontSize="2xs"
                            rounded="full"
                            px={1.5}
                            pointerEvents="none"
                          >
                            pronto
                          </Badge>
                        )}
                      </Box>
                    </Tooltip>
                  )}
                  {showTuWebInColabHeader && (
                    <Tooltip
                      content={isTuWebTabActive ? 'Sección activa' : 'Ir a Tu web'}
                      showArrow
                      bg="gray.700"
                      color="white"
                      positioning={{
                        placement: "bottom"
                      }}
                    >
                      <Button
                        variant={isTuWebTabActive ? 'solid' : 'ghost'}
                        bg={isTuWebTabActive ? 'purple.600' : 'transparent'}
                        color={isTuWebTabActive ? 'white' : 'gray.700'}
                        size="md"
                        rounded="full"
                        onClick={() => handleNavigation(BANDA_TU_WEB_TAB_PATH)}
                        fontWeight="600"
                        _hover={{
                          bg: isTuWebTabActive ? 'purple.700' : 'gray.100',
                          transform: 'scale(1.02)',
                        }}
                        _active={{ transform: 'scale(0.98)' }}
                        transition="all 0.2s"><Icon asChild><FaGlobe /></Icon>Tu web
                                              </Button>
                    </Tooltip>
                  )}
                </HStack>

                <HStack gap={2} ml="auto">
                  <IconButton
                    aria-label="Abrir menú"
                    variant="ghost"
                    size="md"
                    display={{ base: 'flex', md: 'none' }}
                    onClick={onOpen}><FaBars /></IconButton>
                  <Menu.Root>
                    <Menu.Trigger
                      aria-label="Menú de usuario"
                      borderRadius="full"
                      _focusVisible={{ boxShadow: 'outline' }}>
                      <Avatar.Root size="sm" bg="purple.100" color="purple.600"><Avatar.Fallback name={user?.username || userName} /><Avatar.Image src={userAvatar} /></Avatar.Root>
                    </Menu.Trigger>
                    <Portal><Menu.Positioner><Menu.Content>
                          {userType === 'banda' && (
                            <Menu.Item
                              icon={<Icon asChild><FaCrown /></Icon>}
                              onSelect={() => handleNavigation(`${routePrefix}/suscripcion`)}
                              value='item-0'>
                              Mi suscripción
                            </Menu.Item>
                          )}
                          <Menu.Item
                            icon={<Icon asChild><FaSignOutAlt /></Icon>}
                            onSelect={() => { trackLogout(); logout(); navigate('/'); }}
                            value='item-1'>
                            Cerrar sesión
                          </Menu.Item>
                        </Menu.Content></Menu.Positioner></Portal>
                  </Menu.Root>
                </HStack>
              </>
            ) : (
              <>
                {/* Navigation */}
                <HStack gap={1} display={{ base: 'none', md: 'flex' }}>
                  {navigationItems.filter(item => item.enabled).map((item) => (
                    <Tooltip
                      key={item.path}
                      content={`Ir a ${item.label}`}
                      showArrow
                      bg="gray.700"
                      color="white"
                      positioning={{
                        placement: "bottom"
                      }}
                    >
                      <Box position="relative">
                        <Button
                          variant={location.pathname === item.path ? 'solid' : 'ghost'}
                          bg={location.pathname === item.path ? 'purple.600' : 'transparent'}
                          color={location.pathname === item.path ? 'white' : 'gray.700'}
                          size="md"
                          rounded="full"
                          onClick={() => handleNavigation(item.path)}
                          fontWeight="600"
                          _hover={{
                            bg: location.pathname === item.path ? 'purple.700' : 'gray.100',
                            transform: 'scale(1.02)',
                          }}
                          _active={{
                            transform: 'scale(0.98)',
                          }}
                          position="relative"
                          overflow="visible"
                          transition="all 0.2s"><Icon asChild><item.icon /></Icon>{item.label}</Button>
                        {item.badge && (
                          <Badge
                            colorPalette="red"
                            position="absolute"
                            top="-1"
                            right="-1"
                            fontSize="xs"
                            rounded="full"
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </Box>
                    </Tooltip>
                  ))}
                  {userType === 'banda' && (
                    <Tooltip
                      content={isMvpMode ? 'Próximamente disponible' : isGestionActive ? 'Sección activa' : 'Abrir Gestión'}
                      showArrow
                      bg="gray.700"
                      color="white"
                      positioning={{
                        placement: "bottom"
                      }}
                    >
                      <Box position="relative" display="inline-flex">
                        <Button
                          variant={isGestionActive && !isMvpMode ? 'solid' : 'ghost'}
                          bg={isGestionActive && !isMvpMode ? 'purple.600' : 'transparent'}
                          color={isMvpMode ? 'gray.400' : isGestionActive ? 'white' : 'gray.700'}
                          size="md"
                          rounded="full"
                          onClick={() => handleNavigation(gestionItems[0].path)}
                          cursor="pointer"
                          fontWeight="600"
                          opacity={isMvpMode ? 0.6 : 1}
                          _hover={{
                            bg: isMvpMode ? 'gray.100' : isGestionActive ? 'purple.600' : 'gray.100',
                            transform: 'scale(1.02)',
                          }}
                          _active={{ transform: 'scale(0.98)' }}
                          transition="all 0.2s"><Icon asChild><FaFolderOpen /></Icon>Gestión
                                                  </Button>
                        {isMvpMode && (
                          <Badge
                            position="absolute"
                            top="-6px"
                            right="-6px"
                            colorPalette="orange"
                            fontSize="2xs"
                            rounded="full"
                            px={1.5}
                            pointerEvents="none"
                          >
                            pronto
                          </Badge>
                        )}
                      </Box>
                    </Tooltip>
                  )}
                </HStack>

                {/* Mobile menu button */}
                <IconButton
                  aria-label="Abrir menú"
                  variant="ghost"
                  size="md"
                  display={{ base: 'flex', md: 'none' }}
                  onClick={onOpen}><FaBars /></IconButton>

                {/* User Menu */}
                <HStack gap={4} display={{ base: 'none', md: 'flex' }}>
                  {hasBandaTour(location.pathname) && (
                    <Tooltip content="Guía de uso" showArrow>
                      <Button
                        variant="ghost"
                        size="sm"
                        p={0}
                        minW="auto"
                        h="auto"
                        aria-label="Guía de uso"
                        data-testid="guia-uso-btn"
                        onClick={() => window.dispatchEvent(new Event('start-product-tour'))}
                        color="purple.500"
                        _hover={{ color: 'purple.700' }}
                      >
                        <Icon w={5} h={5} asChild><FaQuestionCircle /></Icon>
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip content="Notificaciones" showArrow>
                    <Button
                      variant="ghost"
                      size="sm"
                      p={0}
                      minW="auto"
                      h="auto"
                      onClick={onNotificationsOpen}
                      position="relative"
                    >
                      <Icon w={5} h={5} asChild><FaBell /></Icon>
                      {unreadNotifications > 0 && (
                        <Badge
                          colorPalette="red"
                          position="absolute"
                          top="-1"
                          right="-1"
                          fontSize="2xs"
                          rounded="full"
                          w={4}
                          h={4}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {unreadNotifications > 9 ? '9+' : unreadNotifications}
                        </Badge>
                      )}
                    </Button>
                  </Tooltip>

                  <Menu.Root>
                    <Menu.Trigger>
                      <HStack gap={2} cursor="pointer">
                        <Avatar.Root size="sm" bg="purple.100" color="purple.600"><Avatar.Fallback name={user?.username || userName} /><Avatar.Image src={userAvatar} /></Avatar.Root>
                        {user?.username && (
                          <VStack gap={0} align="start" display={{ base: 'none', sm: 'flex' }}>
                            <Text fontSize="sm" fontWeight="medium" lineHeight="1">
                              {user.username}
                            </Text>
                          </VStack>
                        )}
                      </HStack>
                    </Menu.Trigger>
                    <Portal><Menu.Positioner><Menu.Content>
                          <Menu.Item
                            icon={<Icon asChild><FaUser /></Icon>}
                            onSelect={() => handleNavigation(`${routePrefix}/perfil`)}
                            value='item-2'>
                            Mi perfil
                          </Menu.Item>
                          {userType === 'banda' && (
                            <Menu.Item
                              icon={<Icon asChild><FaCrown /></Icon>}
                              onSelect={() => handleNavigation(`${routePrefix}/suscripcion`)}
                              value='item-3'>
                              Mi suscripción
                            </Menu.Item>
                          )}
                          <Menu.Separator />
                          <Menu.Item
                            icon={<Icon asChild><FaSignOutAlt /></Icon>}
                            onSelect={() => { trackLogout(); logout(); navigate('/'); }}
                            value='item-4'>
                            Cerrar sesión
                          </Menu.Item>
                        </Menu.Content></Menu.Positioner></Portal>
                  </Menu.Root>
                </HStack>
              </>
            )}
          </Flex>
        </Container>
      </Box>

      {/* Breadcrumbs */}
      {!isGuestPublic && (
        <Box bg={bg} borderBottom="1px" borderColor={borderColor}>
          <Container maxW="7xl">
            <Flex h={12} alignItems="center">
              <Breadcrumb.Root fontSize="sm">
                <Breadcrumb.List gap="8px">{getBreadcrumbs().map((crumb, index) => (
                    <Breadcrumb.Item key={`${crumb.label}-${index}`}>
                      <Breadcrumb.Link
                        onClick={() => crumb.path && handleNavigation(crumb.path)}
                        cursor={crumb.path ? 'pointer' : 'default'}
                        color={index === getBreadcrumbs().length - 1 ? 'purple.600' : textColor}
                        fontWeight={index === getBreadcrumbs().length - 1 ? 'medium' : 'normal'}
                      >
                        {crumb.label}
                      </Breadcrumb.Link>
                    </Breadcrumb.Item>
                  ))}</Breadcrumb.List>
              </Breadcrumb.Root>
            </Flex>
          </Container>
        </Box>
      )}

      {/* Main Content */}
      {isGestionActive ? (
        <>
          {/* Mobile: horizontal tab strip */}
          <Box
            display={{ base: 'flex', md: 'none' }}
            bg={bg}
            borderBottom="1px"
            borderColor={borderColor}
            overflowX="auto"
            px={2}
            py={1}
            gap={1}
          >
            {visibleGestionItems.map((item) => {
              const active = location.pathname.startsWith(item.path);
              return (
                <Button
                  key={item.path}
                  size="sm"
                  variant={active ? 'solid' : 'ghost'}
                  colorPalette={active ? 'purple' : 'gray'}
                  onClick={() => handleNavigation(item.path)}
                  rounded="full"
                  flexShrink={0}
                  fontWeight="600"><Icon asChild><item.icon /></Icon>{item.label}</Button>
              );
            })}
          </Box>

          {/* Desktop: persistent sidebar + content */}
          <Flex align="flex-start">
            <Box
              display={{ base: 'none', md: 'block' }}
              w="220px"
              flexShrink={0}
              borderRight="1px"
              borderColor={borderColor}
              bg={bg}
              position="sticky"
              top="112px"
              h="calc(100vh - 112px)"
              overflowY="auto"
              py={4}
              px={2}
            >
              <Text
                fontSize="2xs"
                fontWeight="700"
                color="gray.400"
                px={3}
                mb={3}
                textTransform="uppercase"
                letterSpacing="widest"
              >
                Gestión
              </Text>
              <VStack gap={1} align="stretch">
                {visibleGestionItems.map((item) => {
                  const active = location.pathname.startsWith(item.path);
                  return (
                    <Button
                      key={item.path}
                      variant={active ? 'solid' : 'ghost'}
                      colorPalette={active ? 'purple' : 'gray'}
                      justifyContent="flex-start"
                      h="auto"
                      py={3}
                      px={3}
                      rounded="xl"
                      onClick={() => handleNavigation(item.path)}
                      _hover={{
                        bg: active ? 'purple.700' : 'gray.100',
                      }}
                      transition="all 0.15s"
                    >
                      <HStack gap={3} w="full" align="center">
                        <Box
                          p={1.5}
                          rounded="md"
                          bg={active ? 'whiteAlpha.300' : `${item.color}.50`}
                          flexShrink={0}
                        >
                          <Icon color={active ? 'white' : `${item.color}.500`} boxSize={3.5} asChild><item.icon /></Icon>
                        </Box>
                        <VStack gap={0} align="start">
                          <Text fontWeight="700" fontSize="sm" lineHeight="1.2">
                            {item.label}
                          </Text>
                          {active && (
                            <Text
                              fontSize="2xs"
                              fontWeight="normal"
                              opacity={0.8}
                              lineHeight="1.3"
                              mt={0.5}
                              whiteSpace="normal"
                              textAlign="left"
                            >
                              {item.desc}
                            </Text>
                          )}
                        </VStack>
                      </HStack>
                    </Button>
                  );
                })}
              </VStack>
            </Box>

            {/* Page content */}
            <Box flex={1} minW={0}>
              {children}
            </Box>
          </Flex>
        </>
      ) : (
        <Box>
          {children}
        </Box>
      )}

      {/* Mobile Navigation Drawer */}
      <Drawer.Root open={open} placement='end' onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.CloseTrigger />
              <Drawer.Header>
                <Image 
                  src="/logo y texto cadencya.png" 
                  alt="Cadencya" 
                  h="40px"
                  w="auto"
                />
              </Drawer.Header>

              <Drawer.Body>
                {isColaboradorBanda && userType === 'banda' ? (
                  <VStack gap={4} align="stretch">
                    <VStack gap={2} p={5} bg="purple.50" rounded="2xl" align="start">
                      <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                        Modo colaborador
                      </Text>
                      <Text fontWeight="700" fontSize="md" lineClamp={2}>
                        {colabEntityLabel}
                      </Text>
                      {user?.username && (
                        <Text fontSize="sm" color="gray.600">
                          {user.username}
                        </Text>
                      )}
                    </VStack>
                    <Stack gap={2}>
                      {showGestionInColabHeader && (
                        <Button
                          variant={isGestionActive && !isMvpMode ? 'solid' : 'ghost'}
                          bg={isGestionActive && !isMvpMode ? 'purple.600' : 'transparent'}
                          color={isMvpMode ? 'gray.400' : isGestionActive ? 'white' : 'gray.700'}
                          justifyContent="flex-start"
                          opacity={isMvpMode ? 0.7 : 1}
                          onClick={() => {
                            onClose();
                            if (!isMvpMode) handleNavigation(firstVisibleGestionPath);
                          }}
                          size="lg"
                          h={12}
                          rounded="xl"
                          fontWeight="600"
                          _hover={{
                            bg: isMvpMode ? 'gray.100' : isGestionActive ? 'purple.700' : 'gray.100',
                            transform: 'translateX(4px)',
                          }}
                          transition="all 0.2s"><Icon asChild><FaFolderOpen /></Icon>Gestión
                                              </Button>
                      )}
                      {showTuWebInColabHeader && (
                        <Button
                          variant={isTuWebTabActive ? 'solid' : 'ghost'}
                          bg={isTuWebTabActive ? 'purple.600' : 'transparent'}
                          color={isTuWebTabActive ? 'white' : 'gray.700'}
                          justifyContent="flex-start"
                          onClick={() => {
                            onClose();
                            handleNavigation(BANDA_TU_WEB_TAB_PATH);
                          }}
                          size="lg"
                          h={12}
                          rounded="xl"
                          fontWeight="600"
                          _hover={{
                            bg: isTuWebTabActive ? 'purple.700' : 'gray.100',
                            transform: 'translateX(4px)',
                          }}
                          transition="all 0.2s"><Icon asChild><FaGlobe /></Icon>Tu web
                                              </Button>
                      )}
                    </Stack>
                  </VStack>
                ) : (
                  <VStack gap={4} align="stretch">
                    {/* User Info */}
                    <VStack gap={3} p={6} bg="purple.50" rounded="2xl">
                      <Avatar.Root size="lg" bg="purple.600" color="white"><Avatar.Fallback name={user?.username || userName} /><Avatar.Image src={userAvatar} /></Avatar.Root>
                      {user?.username ? (
                        <VStack gap={0}>
                          <Text fontWeight="700" fontSize="lg">{user.username}</Text>
                          <Text fontSize="sm" color="gray.600" textTransform="capitalize">
                            {userType === 'banda' ? 'Banda' : 'Cofradía'}
                          </Text>
                        </VStack>
                      ) : (
                        <Text fontSize="sm" color="gray.600" textTransform="capitalize">
                          {userType === 'banda' ? 'Banda' : 'Cofradía'}
                        </Text>
                      )}
                    </VStack>

                    {/* Navigation Items */}
                    <Stack gap={2}>
                      {navigationItems.filter(item => item.enabled).map((item) => (
                        <Button
                          key={item.path}
                          variant={location.pathname === item.path ? 'solid' : 'ghost'}
                          bg={location.pathname === item.path ? 'purple.600' : 'transparent'}
                          color={location.pathname === item.path ? 'white' : 'gray.700'}
                          justifyContent="flex-start"
                          onClick={() => {
                            handleNavigation(item.path);
                            onClose();
                          }}
                          position="relative"
                          size="lg"
                          h={12}
                          rounded="xl"
                          fontWeight="600"
                          _hover={{
                            bg: location.pathname === item.path ? 'purple.700' : 'gray.100',
                            transform: 'translateX(4px)'
                          }}
                          transition="all 0.2s"><Icon asChild><item.icon /></Icon><HStack w="full" justify="space-between">
                            <Text>{item.label}</Text>
                            {item.badge && (
                              <Badge colorPalette="red" fontSize="xs" rounded="full">
                                {item.badge}
                              </Badge>
                            )}
                          </HStack></Button>
                      ))}
                      {userType === 'banda' && (
                        <Button
                          variant={isGestionActive ? 'solid' : 'ghost'}
                          bg={isGestionActive ? 'purple.600' : 'transparent'}
                          color={isGestionActive ? 'white' : 'gray.700'}
                          justifyContent="flex-start"
                          onClick={() => {
                            onClose();
                            if (!isGestionActive) handleNavigation(gestionItems[0].path);
                          }}
                          size="lg"
                          h={12}
                          rounded="xl"
                          fontWeight="600"
                          _hover={{
                            bg: isGestionActive ? 'purple.700' : 'gray.100',
                            transform: 'translateX(4px)'
                          }}
                          transition="all 0.2s"><Icon asChild><FaFolderOpen /></Icon>Gestión
                                              </Button>
                      )}
                    </Stack>

                    {hasBandaTour(location.pathname) && (
                      <Button
                        variant="outline"
                        colorPalette="purple"
                        size="lg"
                        h={12}
                        rounded="xl"
                        fontWeight="600"
                        w="full"
                        onClick={() => {
                          onClose();
                          setTimeout(() => window.dispatchEvent(new Event('start-product-tour')), 300);
                        }}><Icon asChild><FaQuestionCircle /></Icon>Guía de uso
                                          </Button>
                    )}
                  </VStack>
                )}
              </Drawer.Body>

              <Drawer.Footer>
                <VStack w="full" gap={3}>
                  {userType === 'banda' && (
                    <Button
                      variant="outline"
                      colorPalette="purple"
                      w="full"
                      rounded="full"
                      onClick={() => handleNavigation(`${routePrefix}/suscripcion`)}><Icon asChild><FaCrown /></Icon>Mi suscripción
                                      </Button>
                  )}
                  <Button
                    bg="red.500"
                    color="white"
                    w="full"
                    rounded="full"
                    onClick={() => { trackLogout(); logout(); navigate('/'); }}
                    _hover={{ bg: 'red.600' }}><Icon asChild><FaSignOutAlt /></Icon>Cerrar sesión
                                  </Button>
                </VStack>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>

        </Portal>
      </Drawer.Root>

      {/* Gestión Drawer */}
      <Drawer.Root open={isGestionOpen} placement='start' size='xs' onOpenChange={e => {
        if (!e.open) {
          onGestionClose();
        }
      }}>
        <Portal>

          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.CloseTrigger />
              <Drawer.Header borderBottomWidth="1px" pb={4}>
                <HStack gap={3}>
                  <Icon color="purple.600" w={5} h={5} asChild><FaFolderOpen /></Icon>
                  <VStack gap={0} align="start">
                    <Text fontSize="lg" fontWeight="bold">Gestión</Text>
                    <Text fontSize="xs" color="gray.500" fontWeight="normal">
                      Herramientas de administración
                    </Text>
                  </VStack>
                </HStack>
              </Drawer.Header>

              <Drawer.Body px={3} py={4}>
                <VStack gap={2} align="stretch">
                  {visibleGestionItems.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    return (
                      <Button
                        key={item.path}
                        variant={isActive ? 'solid' : 'ghost'}
                        colorPalette={isActive ? 'purple' : 'gray'}
                        justifyContent="flex-start"
                        h="auto"
                        py={4}
                        px={4}
                        rounded="xl"
                        onClick={() => {
                          handleNavigation(item.path);
                          onGestionClose();
                        }}
                        _hover={{
                          bg: isActive ? 'purple.700' : 'gray.100',
                          transform: 'translateX(3px)',
                        }}
                        transition="all 0.2s"
                      >
                        <HStack gap={3} w="full" align="center">
                          <Box
                            p={2}
                            rounded="lg"
                            bg={isActive ? 'whiteAlpha.300' : `${item.color}.50`}
                            flexShrink={0}
                          >
                            <Icon color={isActive ? 'white' : `${item.color}.500`} boxSize={4} asChild><item.icon /></Icon>
                          </Box>
                          <VStack gap={0} align="start" flex={1}>
                            <Text fontWeight="700" fontSize="sm" lineHeight="1.2">
                              {item.label}
                            </Text>
                            <Text
                              fontSize="xs"
                              fontWeight="normal"
                              opacity={isActive ? 0.85 : 0.6}
                              lineHeight="1.3"
                              mt={0.5}
                              whiteSpace="normal"
                              textAlign="left"
                            >
                              {item.desc}
                            </Text>
                          </VStack>
                        </HStack>
                      </Button>
                    );
                  })}
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>

        </Portal>
      </Drawer.Root>

      {/* Notifications Drawer */}
      <Drawer.Root open={isNotificationsOpen} placement='end' size='md' onOpenChange={e => {
        if (!e.open) {
          onNotificationsClose();
        }
      }}>
        <Portal>

          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.CloseTrigger />
              <Drawer.Header borderBottomWidth="1px">
                <HStack gap={3}>
                  <Icon color="purple.600" w={6} h={6} asChild><FaBell /></Icon>
                  <VStack gap={0} align="start">
                    <Text fontSize="lg" fontWeight="bold">Notificaciones</Text>
                    <Text fontSize="sm" color={textColor} fontWeight="normal">
                      {unreadNotifications > 0 
                        ? `Tienes ${unreadNotifications} notificación${unreadNotifications > 1 ? 'es' : ''} nueva${unreadNotifications > 1 ? 's' : ''}`
                        : 'No tienes notificaciones nuevas'
                      }
                    </Text>
                  </VStack>
                </HStack>
              </Drawer.Header>

              <Drawer.Body p={0}>
                {isLoadingNotifications ? (
                  <Box display="flex" justifyContent="center" alignItems="center" h="200px">
                    <Spinner size="lg" color="purple.500" />
                  </Box>
                ) : notifications.length === 0 ? (
                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" h="200px" p={8}>
                    <Icon w={12} h={12} color="gray.300" mb={4} asChild><FaBell /></Icon>
                    <Text color="gray.500" textAlign="center">No tienes notificaciones</Text>
                  </Box>
                ) : (
                  <VStack gap={0} align="stretch">
                    {notifications.map((notification) => {
                      const { icon: NotifIcon, color } = getNotificationIcon(notification.type);
                      return (
                        <Box
                          key={notification.id}
                          p={4}
                          borderBottom="1px"
                          borderColor={borderColor}
                          bg={!notification.isRead ? `${color}.50` : 'transparent'}
                          cursor="pointer"
                          _hover={{ bg: !notification.isRead ? `${color}.100` : 'gray.50' }}
                          transition="background 0.2s"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <HStack gap={3} align="start">
                            <Icon color={`${color}.600`} w={5} h={5} mt={1} asChild><NotifIcon /></Icon>
                            <VStack gap={1} align="start" flex={1}>
                              <Text fontSize="sm" fontWeight={!notification.isRead ? 'bold' : 'medium'}>
                                {notification.title || 'Notificación'}
                              </Text>
                              <Text fontSize="xs" color={textColor}>
                                {notification.message}
                              </Text>
                              <Text fontSize="2xs" color="gray.500">
                                {formatTimeAgo(notification.createdAt)}
                              </Text>
                            </VStack>
                            {!notification.isRead && (
                              <Box w={2} h={2} bg={`${color}.600`} rounded="full" />
                            )}
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </Drawer.Body>

              <Drawer.Footer borderTopWidth="1px">
                <Button
                  variant="ghost"
                  colorPalette="purple"
                  w="full"
                  onClick={handleMarkAllAsRead}
                  disabled={unreadNotifications === 0}
                >
                  Marcar todas como leídas
                </Button>
              </Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>

        </Portal>
      </Drawer.Root>

      {/* Footer */}
      <Footer />

      {/* Floating support widget */}
      <SupportWidget />
    </Box>
  );
};

export default AppLayout;
